import React, {useState, useEffect, useMemo, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {apiService} from '../../services/api_url';
import {useUserData} from '../../services/useUserData';
import Loader from '../../widgets/Loader/Loader';
import BackgroundCanvasWithoutRadar from '../../widgets/canvas/BackgroundCanvasWithoutRadar';
import {BackIcon} from '../../assets/images/BackIcon';
import './PaymentPage.scss';

interface PeriodOption {
    months: number;
    name: string;
    popular?: boolean;
}

interface PaymentPageProps {
    onModalStateChange?: (isOpen: boolean) => void;
}

type AppEnv = Record<string, string | number | undefined>;
type PeriodPriceMap = Record<number, number>;

interface PricingConfig {
    oneDevicePeriodPrices: PeriodPriceMap;
    threeDevicePeriodPrices: PeriodPriceMap;
    baseMonthPrice: number;
}

const PERIOD_OPTIONS: PeriodOption[] = [
    {months: 1, name: '1 месяц'},
    {months: 3, name: '3 месяца'},
    {months: 6, name: '6 месяцев', popular: true},
    {months: 12, name: '12 месяцев'}
];

const getRuntimeEnv = (): AppEnv | undefined => {
    return (window as Window & { __APP_ENV__?: AppEnv }).__APP_ENV__;
};

const BUILD_ENV: Record<string, string | undefined> = {
    PAYMENT_1M_1_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_1M_1_DEVICE_PRICE,
    PAYMENT_3M_1_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_3M_1_DEVICE_PRICE,
    PAYMENT_6M_1_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_6M_1_DEVICE_PRICE,
    PAYMENT_12M_1_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_12M_1_DEVICE_PRICE,

    PAYMENT_1M_3_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_1M_3_DEVICE_PRICE,
    PAYMENT_3M_3_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_3M_3_DEVICE_PRICE,
    PAYMENT_6M_3_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_6M_3_DEVICE_PRICE,
    PAYMENT_12M_3_DEVICE_PRICE: process.env.REACT_APP_PAYMENT_12M_3_DEVICE_PRICE,

    CONF_1M_1_DEVICE_PRICE: process.env.REACT_APP_CONF_1M_1_DEVICE_PRICE,
    CONF_3M_1_DEVICE_PRICE: process.env.REACT_APP_CONF_3M_1_DEVICE_PRICE,
    CONF_6M_1_DEVICE_PRICE: process.env.REACT_APP_CONF_6M_1_DEVICE_PRICE,
    CONF_12M_1_DEVICE_PRICE: process.env.REACT_APP_CONF_12M_1_DEVICE_PRICE,

    CONF_1M_3_DEVICE_PRICE: process.env.REACT_APP_CONF_1M_3_DEVICE_PRICE,
    CONF_3M_3_DEVICE_PRICE: process.env.REACT_APP_CONF_3M_3_DEVICE_PRICE,
    CONF_6M_3_DEVICE_PRICE: process.env.REACT_APP_CONF_6M_3_DEVICE_PRICE,
    CONF_12M_3_DEVICE_PRICE: process.env.REACT_APP_CONF_12M_3_DEVICE_PRICE
};

const parseRequiredEnvNumber = (rawValue: unknown, envKey: string): number => {
    const numericValue = Number(rawValue);

    if (!Number.isFinite(numericValue) || numericValue <= 0) {
        throw new Error(`Некорректное значение env: ${envKey}`);
    }

    return numericValue;
};

const readRequiredPriceEnv = (keys: string[]): number => {
    const runtimeEnv = getRuntimeEnv();

    for (const key of keys) {
        const runtimeValue = runtimeEnv?.[key];
        if (runtimeValue !== undefined && runtimeValue !== null && `${runtimeValue}`.trim() !== '') {
            return parseRequiredEnvNumber(runtimeValue, key);
        }

        const buildValue = BUILD_ENV[key];
        if (buildValue !== undefined && buildValue !== null && buildValue.trim() !== '') {
            return parseRequiredEnvNumber(buildValue, key);
        }
    }

    throw new Error(`Не найдена env-переменная: ${keys.join(' / ')}`);
};

const resolvePricingConfig = (): PricingConfig => {
    const oneDevicePeriodPrices: PeriodPriceMap = {
        1: readRequiredPriceEnv(['PAYMENT_1M_1_DEVICE_PRICE', 'CONF_1M_1_DEVICE_PRICE']),
        3: readRequiredPriceEnv(['PAYMENT_3M_1_DEVICE_PRICE', 'CONF_3M_1_DEVICE_PRICE']),
        6: readRequiredPriceEnv(['PAYMENT_6M_1_DEVICE_PRICE', 'CONF_6M_1_DEVICE_PRICE']),
        12: readRequiredPriceEnv(['PAYMENT_12M_1_DEVICE_PRICE', 'CONF_12M_1_DEVICE_PRICE'])
    };

    const threeDevicePeriodPrices: PeriodPriceMap = {
        1: readRequiredPriceEnv(['PAYMENT_1M_3_DEVICE_PRICE', 'CONF_1M_3_DEVICE_PRICE']),
        3: readRequiredPriceEnv(['PAYMENT_3M_3_DEVICE_PRICE', 'CONF_3M_3_DEVICE_PRICE']),
        6: readRequiredPriceEnv(['PAYMENT_6M_3_DEVICE_PRICE', 'CONF_6M_3_DEVICE_PRICE']),
        12: readRequiredPriceEnv(['PAYMENT_12M_3_DEVICE_PRICE', 'CONF_12M_3_DEVICE_PRICE'])
    };

    return {
        oneDevicePeriodPrices,
        threeDevicePeriodPrices,
        baseMonthPrice: oneDevicePeriodPrices[1]
    };
};

const roundPrettyPrice = (value: number): number => {
    if (value <= 0) return 0;

    if (value < 200) {
        return Math.round(value);
    }

    if (value < 1000) {
        return Math.max(99, Math.round((value + 1) / 10) * 10 - 1);
    }

    if (value < 3000) {
        return Math.max(99, Math.round((value + 1) / 50) * 50 - 1);
    }

    return Math.max(99, Math.round((value + 1) / 100) * 100 - 1);
};

const clamp = (value: number, min: number, max: number): number => {
    return Math.min(Math.max(value, min), max);
};

const getSingleDevicePeriodPrice = (months: number, oneDevicePeriodPrices: PeriodPriceMap): number => {
    return oneDevicePeriodPrices[months] ?? 0;
};

const getThreeDeviceTargetPrice = (months: number, threeDevicePeriodPrices: PeriodPriceMap): number => {
    return threeDevicePeriodPrices[months] ?? 0;
};

const getAnchorFactor = (
    months: number,
    oneDevicePeriodPrices: PeriodPriceMap,
    threeDevicePeriodPrices: PeriodPriceMap
): number => {
    const singlePrice = getSingleDevicePeriodPrice(months, oneDevicePeriodPrices);
    const threePrice = getThreeDeviceTargetPrice(months, threeDevicePeriodPrices);

    if (!singlePrice || !threePrice) {
        return 1;
    }

    return threePrice / (singlePrice * 3);
};

const getQuantityFactor = (
    months: number,
    count: number,
    oneDevicePeriodPrices: PeriodPriceMap,
    threeDevicePeriodPrices: PeriodPriceMap
): number => {
    if (count <= 1) {
        return 1;
    }

    const anchorFactor = getAnchorFactor(months, oneDevicePeriodPrices, threeDevicePeriodPrices);

    if (count <= 3) {
        const progress = (count - 1) / 2;
        const curvedProgress = Math.pow(progress, 0.85);
        return clamp(1 - (1 - anchorFactor) * curvedProgress, 0.42, 1);
    }

    const extraDevices = count - 3;
    const extraDiscount =
        Math.min(extraDevices, 4) * 0.025 +
        Math.max(extraDevices - 4, 0) * 0.015;

    return clamp(anchorFactor - extraDiscount, 0.42, 1);
};

const getDisplayPrice = (
    months: number,
    count: number,
    oneDevicePeriodPrices: PeriodPriceMap,
    threeDevicePeriodPrices: PeriodPriceMap
): number => {
    if (count <= 0) return 0;

    const singlePrice = getSingleDevicePeriodPrice(months, oneDevicePeriodPrices);

    if (!singlePrice) {
        return 0;
    }

    if (count === 1) {
        return singlePrice;
    }

    if (count === 3) {
        return getThreeDeviceTargetPrice(months, threeDevicePeriodPrices);
    }

    const quantityFactor = getQuantityFactor(
        months,
        count,
        oneDevicePeriodPrices,
        threeDevicePeriodPrices
    );

    return roundPrettyPrice(singlePrice * count * quantityFactor);
};

const getOldPrice = (months: number, count: number, baseMonthPrice: number): number => {
    return Math.round(baseMonthPrice * months * count);
};

const getDiscountPercent = (currentPrice: number, oldPrice: number): number => {
    if (oldPrice <= 0 || currentPrice >= oldPrice) {
        return 0;
    }

    return Math.max(0, Math.round((1 - currentPrice / oldPrice) * 100));
};

function PaymentPage({onModalStateChange}: PaymentPageProps) {
    const navigate = useNavigate();
    const sliderRef = useRef<HTMLDivElement>(null);

    const {telegramId, loading: userLoading, isFromTelegram} = useUserData();

    const [isDragging, setIsDragging] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedDevices, setSelectedDevices] = useState<number>(1);
    const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption | null>(null);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('card');
    const [processing, setProcessing] = useState<boolean>(false);
    const [paymentError, setPaymentError] = useState<string | null>(null);

    const [promoCode, setPromoCode] = useState<string>('');
    const [promoApplied, setPromoApplied] = useState<boolean>(false);
    const [promoMessage, setPromoMessage] = useState<string>('');
    const [promoDiscount, setPromoDiscount] = useState<number>(0);
    const [finalPrice, setFinalPrice] = useState<number>(0);
    const [applyingPromo, setApplyingPromo] = useState<boolean>(false);

    const [pageReady, setPageReady] = useState(false);

    const pricingConfig = useMemo(() => {
        try {
            return {
                data: resolvePricingConfig(),
                error: null as string | null
            };
        } catch (error) {
            return {
                data: null as PricingConfig | null,
                error: error instanceof Error ? error.message : 'Ошибка конфигурации цен'
            };
        }
    }, []);

    const baseMonthPrice = pricingConfig.data?.baseMonthPrice ?? 0;
    const oneDevicePeriodPrices = pricingConfig.data?.oneDevicePeriodPrices ?? {};
    const threeDevicePeriodPrices = pricingConfig.data?.threeDevicePeriodPrices ?? {};

    const sliderPosition = useMemo(() => (selectedDevices - 1) * (100 / 9), [selectedDevices]);

    const totalPrice = useMemo(() => {
        if (!selectedPeriod || !pricingConfig.data) return 0;

        return getDisplayPrice(
            selectedPeriod.months,
            selectedDevices,
            oneDevicePeriodPrices,
            threeDevicePeriodPrices
        );
    }, [selectedPeriod, selectedDevices, pricingConfig.data, oneDevicePeriodPrices, threeDevicePeriodPrices]);

    const oldTotalPrice = useMemo(() => {
        if (!selectedPeriod || !pricingConfig.data) return 0;
        return getOldPrice(selectedPeriod.months, selectedDevices, baseMonthPrice);
    }, [selectedPeriod, selectedDevices, pricingConfig.data, baseMonthPrice]);

    const tariffDiscount = useMemo(() => {
        return getDiscountPercent(totalPrice, oldTotalPrice);
    }, [totalPrice, oldTotalPrice]);

    const displayPrice = promoApplied ? finalPrice : totalPrice;

    useEffect(() => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
            tg.ready();
            tg.expand();
            tg.setHeaderColor?.('#000000');
            tg.setBackgroundColor?.('#000000');
        }
        setPageReady(true);
    }, []);

    useEffect(() => {
        if (!promoApplied) {
            setFinalPrice(totalPrice);
            return;
        }

        setPromoApplied(false);
        setPromoMessage('');
        setPromoDiscount(0);
        setFinalPrice(totalPrice);
    }, [selectedDevices, selectedPeriod?.months, totalPrice]);
    const updateSliderPosition = (clientX: number) => {
        if (!sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const width = rect.width;

        const percent = Math.max(0, Math.min(100, (x / width) * 100));
        const deviceIndex = Math.floor((percent / 100) * 9);
        const devices = deviceIndex + 1;

        setSelectedDevices(devices);
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        updateSliderPosition(e.clientX);
    };

    const handleMouseMove = (e: MouseEvent) => {
        e.preventDefault();
        if (isDragging) {
            updateSliderPosition(e.clientX);
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        e.preventDefault();
        setIsDragging(true);
        const touch = e.touches[0];
        updateSliderPosition(touch.clientX);
    };

    const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        if (isDragging) {
            const touch = e.touches[0];
            updateSliderPosition(touch.clientX);
        }
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleTouchMove, {passive: false});
            window.addEventListener('touchend', handleTouchEnd);
            window.addEventListener('touchcancel', handleTouchEnd);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
            window.removeEventListener('touchcancel', handleTouchEnd);
        };
    }, [isDragging]);

    const handlePeriodSelect = (period: PeriodOption) => {
        setSelectedPeriod(period);
    };

    const handlePaymentMethodSelect = (method: string) => {
        setSelectedPaymentMethod(method);
    };

    const handleContinueToPayment = () => {
        if (!selectedPeriod) {
            alert('Пожалуйста, выберите период подписки');
            return;
        }

        if (!telegramId) {
            alert('Ошибка авторизации. Пожалуйста, войдите в систему');
            navigate('/key');
            return;
        }

        setShowPaymentModal(true);
        onModalStateChange?.(true);
    };

    const handleCloseModal = () => {
        setShowPaymentModal(false);
        onModalStateChange?.(false);
        setPaymentError(null);
        setPromoCode('');
        setPromoApplied(false);
        setPromoMessage('');
        setPromoDiscount(0);
        setFinalPrice(totalPrice);
    };

    const handleApplyPromo = async () => {
        if (!promoCode.trim() || !telegramId) {
            setPromoMessage('Введите промокод');
            return;
        }

        setApplyingPromo(true);
        setPromoMessage('');

        try {
            const response = await apiService.applyPromoPayment({
                user_id: telegramId,
                promo_code: promoCode,
                amount: totalPrice
            });

            if (response.success) {
                setPromoApplied(true);
                setPromoMessage(response.message);
                setFinalPrice(
                    response.new_amount !== undefined && response.new_amount !== null
                        ? response.new_amount
                        : totalPrice
                );
                setPromoDiscount(response.discount || 0);
            } else {
                setPromoApplied(false);
                setPromoMessage(response.message);
                setFinalPrice(totalPrice);
                setPromoDiscount(0);
            }
        } catch (error) {
            setPromoMessage('Ошибка при проверке промокода');
            setPromoApplied(false);
        } finally {
            setApplyingPromo(false);
        }
    };

    const handleCryptoPay = async () => {
        if (!telegramId || !selectedPeriod) {
            alert('Ошибка: отсутствуют данные для оплаты');
            return;
        }

        setProcessing(true);
        setPaymentError(null);

        try {
            const response = await apiService.createCryptoPayment({
                user_id: telegramId,
                device_count: selectedDevices,
                months: selectedPeriod.months,
                amount: displayPrice,
                network: 'TRX',
                currency: 'USDT'
            });

            if (response.success && response.heleket_response?.result?.url) {
                window.location.href = response.heleket_response.result.url;
            } else {
                setPaymentError('Ошибка при создании платежа');
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.detail || error.message || 'Ошибка при создании крипто-платежа';
            setPaymentError(errorMessage);
        } finally {
            setProcessing(false);
        }
    };

    const handlePay = async () => {
        if (selectedPaymentMethod === 'crypto') {
            await handleCryptoPay();
            return;
        }

        if (!telegramId || !selectedPeriod) {
            alert('Ошибка: отсутствуют данные для оплаты');
            return;
        }

        setProcessing(true);
        setPaymentError(null);

        try {
            const normalizedPromoCode =
                promoApplied && promoCode.trim()
                    ? promoCode.trim().toUpperCase()
                    : undefined;

            const paymentResponse = await apiService.createPayment({
                user_id: telegramId,
                device_count: selectedDevices,
                months: selectedPeriod.months,
                payment_method: selectedPaymentMethod,
                promo_code: normalizedPromoCode
            });

            if (paymentResponse?.payment_url) {
                if (selectedPaymentMethod === 'sbp') {
                    if (isFromTelegram) {
                        const tg = (window as any).Telegram?.WebApp;

                        if (tg) {
                            tg.openLink(paymentResponse.payment_url);
                        }
                        handleCloseModal();
                        navigate(`/payment/success`, {
                            state: {
                                payment_id: paymentResponse.payment_id
                            }
                        });
                    } else {
                        // В обычном браузере - обычный редирект
                        window.location.href = paymentResponse.payment_url;
                    }
                } else {
                    window.location.href = paymentResponse.payment_url;
                }
            } else {
                setPaymentError('Ошибка: не получен URL для оплаты');
            }
        } catch (error: any) {
            setPaymentError(`Ошибка: ${error.message || 'Неизвестная ошибка'}`);
        } finally {
            setProcessing(false);
        }
    };

    const getPaymentMethodIcon = (method: string): React.ReactElement => {
        switch (method) {
            case 'card':
                return (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                        />
                        <path d="M22 10H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M6 14H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M12 14H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                );
            case 'sbp':
                return (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                        />
                        <path d="M22 10H2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M6 14H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M12 14H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                );
            case 'crypto':
                return (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                        <path d="M12 6V8M12 16V18M8 12H6M18 12H16" stroke="currentColor" strokeWidth="1.5"
                              strokeLinecap="round"/>
                        <path
                            d="M12 8C9.79 8 8 9.79 8 12C8 14.21 9.79 16 12 16C14.21 16 16 14.21 16 12C16 9.79 14.21 8 12 8Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            fill="none"
                        />
                    </svg>
                );
            default:
                return <span>💳</span>;
        }
    };

    const LoaderIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
             className="spinner">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
            <path d="M12 2C6.477 2 2 6.477 2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  fill="none"/>
        </svg>
    );

    const GiftIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 12V22H4V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
                  strokeLinejoin="round"/>
            <path d="M22 7H2V12H22V7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
                  strokeLinejoin="round"/>
            <path d="M12 22V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <path
                d="M12 7H7.5C6.83696 7 6.20107 6.73661 5.73223 6.26777C5.26339 5.79893 5 5.16304 5 4.5C5 3.83696 5.26339 3.20107 5.73223 2.73223C6.20107 2.26339 6.83696 2 7.5 2C9.5 2 12 7 12 7Z"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path
                d="M12 7H16.5C17.163 7 17.7989 6.73661 18.2678 6.26777C18.7366 5.79893 19 5.16304 19 4.5C19 3.83696 18.7366 3.20107 18.2678 2.73223C17.7989 2.26339 17.163 2 16.5 2C14.5 2 12 7 12 7Z"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );

    const getPaymentMethodName = (method: string): string => {
        switch (method) {
            case 'card':
                return 'Карта';
            case 'sbp':
                return 'СБП';
            case 'crypto':
                return 'Крипта';
            default:
                return method;
        }
    };

    if (userLoading && !pageReady) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar/>
                <div className="payment-page loading">
                    <div className="loader"><Loader/></div>
                </div>
            </div>
        );
    }

    if (pricingConfig.error) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar/>
                <div className="payment-page">
                    <div className="payment-header">
                        <button className="back-button" onClick={() => navigate('/')}>
                            <BackIcon/>
                        </button>
                        <h1 className="payment-title">Оформление</h1>
                    </div>
                    <div className="error-container">
                        <div className="error-icon">⚙️</div>
                        <h2>Не настроены цены</h2>
                        <p>{pricingConfig.error}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!telegramId && pageReady) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar/>
                <div className="payment-page">
                    <div className="payment-header">
                        <button className="back-button" onClick={() => navigate('/')}>
                            <BackIcon/>
                        </button>
                        <h1 className="payment-title">Оформление</h1>
                    </div>
                    <div className="error-container">
                        <div className="error-icon">🔒</div>
                        <h2>Необходима авторизация</h2>
                        <p>Для оформления подписки нужно войти в систему</p>
                        <button className="continue-button" onClick={() => navigate('/key')}>
                            Перейти к авторизации
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="tg-app">
            <BackgroundCanvasWithoutRadar/>

            <div className="payment-page">
                <div className="payment-header">
                    <button className="back-button" onClick={() => navigate('/')}>
                        <BackIcon/>
                    </button>
                    <h1 className="payment-title">Оформление</h1>
                </div>

                <div className="pricing-card">
                    <div className="device-slider-container">
                        <div className="slider-value-display">
                            <div className="slider-value">{selectedDevices}</div>
                            <div className="slider-label-wrapper">
                                <div className="slider-label">
                                    {selectedDevices === 1 ? 'Устройство' :
                                        selectedDevices >= 2 && selectedDevices <= 4 ? 'Устройства' : 'Устройств'}
                                </div>
                                <div className="slider-label-sub">
                                    Одновременно в подписке
                                </div>
                            </div>
                        </div>

                        <div className="slider-wrapper">
                            <div
                                className="slider-track"
                                ref={sliderRef}
                                onMouseDown={handleMouseDown}
                                onTouchStart={handleTouchStart}
                                style={{touchAction: 'none'}}
                            >
                                <div className="slider-filled" style={{width: `${sliderPosition}%`}}/>
                                <div
                                    className="slider-thumb"
                                    style={{left: `${sliderPosition}%`}}
                                    onMouseDown={handleMouseDown}
                                    onTouchStart={handleTouchStart}
                                />
                            </div>
                        </div>

                        <div className="slider-markers">
                            <span className={`marker ${selectedDevices >= 1 ? 'active' : ''}`}>1</span>
                            <span className={`marker ${selectedDevices >= 2 ? 'active' : ''}`}>2</span>
                            <span className={`marker ${selectedDevices >= 3 ? 'active' : ''}`}>3</span>
                            <span className={`marker ${selectedDevices >= 4 ? 'active' : ''}`}>4</span>
                            <span className={`marker ${selectedDevices >= 5 ? 'active' : ''}`}>5</span>
                            <span className={`marker ${selectedDevices >= 6 ? 'active' : ''}`}>6</span>
                            <span className={`marker ${selectedDevices >= 7 ? 'active' : ''}`}>7</span>
                            <span className={`marker ${selectedDevices >= 8 ? 'active' : ''}`}>8</span>
                            <span className={`marker ${selectedDevices >= 9 ? 'active' : ''}`}>9</span>
                            <span className={`marker ${selectedDevices >= 10 ? 'active' : ''}`}>10</span>
                        </div>
                    </div>
                </div>

                <div className="period-grid">
                    {PERIOD_OPTIONS.map((period) => {
                        const periodTotalPrice = getDisplayPrice(
                            period.months,
                            selectedDevices,
                            oneDevicePeriodPrices,
                            threeDevicePeriodPrices
                        );
                        const oldPrice = getOldPrice(period.months, selectedDevices, baseMonthPrice);
                        const discount = getDiscountPercent(periodTotalPrice, oldPrice);

                        return (
                            <button
                                key={period.months}
                                className={`period-grid-item ${selectedPeriod?.months === period.months ? 'selected' : ''} ${period.popular ? 'popular' : ''}`}
                                onClick={() => handlePeriodSelect(period)}
                            >
                                <div className="period-grid-content">
                                    <div className="period-grid-name">{period.name}</div>
                                    <div className="period-grid-price">
                                        {periodTotalPrice}₽
                                        {oldPrice > periodTotalPrice && (
                                            <span className="period-grid-old-price">{oldPrice}₽</span>
                                        )}
                                    </div>
                                    {discount > 0 && (
                                        <div className="period-grid-save">
                                            −{discount}%
                                        </div>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>

                <button
                    className="continue-button"
                    onClick={handleContinueToPayment}
                    disabled={!selectedPeriod}
                >
                    <span>Продолжить оформление</span>
                    <span className="continue-price">{displayPrice}₽</span>
                </button>

                {showPaymentModal && (
                    <div className="modal-overlay" onClick={handleCloseModal}>
                        <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Оплата подписки</h3>
                                <button className="close-button" onClick={handleCloseModal}>✕</button>
                            </div>

                            <div className="modal-content">
                                <div className="modal-total-section">
                                    <div className="modal-total-row">
                                        <span>Устройств</span>
                                        <span className="modal-value">{selectedDevices}</span>
                                    </div>

                                    <div className="modal-total-row">
                                        <span>Период</span>
                                        <span className="modal-value">{selectedPeriod?.name}</span>
                                    </div>

                                    <div className="modal-total-row total">
                                        <span>Итого</span>
                                        <div className="modal-price-block">
                                            {promoApplied && totalPrice > displayPrice && (
                                                <span className="old-price">{totalPrice}₽</span>
                                            )}
                                            <span className="total-price">{displayPrice}₽</span>
                                        </div>
                                    </div>

                                    {promoApplied && promoDiscount > 0 && (
                                        <div className="modal-discount-row">
                                            <span>Скидка по промокоду</span>
                                            <span className="discount-value">−{promoDiscount}%</span>
                                        </div>
                                    )}

                                    {tariffDiscount > 0 && (
                                        <div className="modal-bonus">
                                            <span className="bonus-icon"><GiftIcon/></span>
                                            <span>Экономия по тарифу {tariffDiscount}%</span>
                                        </div>
                                    )}
                                </div>

                                <div className="modal-promo-section">
                                    <div className="promo-input-group">
                                        <input
                                            type="text"
                                            className="promo-input"
                                            placeholder="Введите промокод"
                                            value={promoCode}
                                            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                            disabled={promoApplied || processing}
                                        />
                                        <button
                                            className={`promo-button ${promoApplied ? 'applied' : ''}`}
                                            onClick={handleApplyPromo}
                                            disabled={!promoCode.trim() || promoApplied || applyingPromo || processing}
                                        >
                                            {applyingPromo ? '⏳' : promoApplied ? '✓' : 'Применить'}
                                        </button>
                                    </div>

                                    {promoMessage && (
                                        <div className={`promo-message-payment ${promoApplied ? 'success' : 'error'}`}>
                                            {promoMessage}
                                        </div>
                                    )}
                                </div>
                                <div className="modal-payment-methods">
                                    <div className="modal-payment-title">Способ оплаты</div>
                                    <div className="modal-payment-grid" style={{gridTemplateColumns: 'repeat(2, 1fr)'}}>
                                        {/*{['card-sbp', 'crypto'].map((method) => (*/}
                                        {['card', 'sbp'].map((method) => (
                                            <button
                                                key={method}
                                                className={`modal-payment-option ${selectedPaymentMethod === method ? 'selected' : ''}`}
                                                onClick={() => handlePaymentMethodSelect(method)}
                                                disabled={processing}
                                            >
                                                <span className="method-icon">{getPaymentMethodIcon(method)}</span>
                                                <span className="method-name">{getPaymentMethodName(method)}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {paymentError && (
                                    <div className="payment-error">
                                        {paymentError}
                                    </div>
                                )}

                                <button
                                    className="modal-pay-button"
                                    onClick={handlePay}
                                    disabled={processing}
                                >
                                    {processing ? (
                                        <>
                                            <span>Обработка</span>
                                            <span><LoaderIcon/></span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Оплатить</span>
                                            <div className="pay-button-prices">
                                                {promoApplied && displayPrice !== totalPrice && (
                                                    <span className="old-price-button">{totalPrice}₽</span>
                                                )}
                                                <span className="final-price-button">{displayPrice}₽</span>
                                            </div>
                                        </>
                                    )}
                                </button>

                                <div className="modal-footer">
                                    <div className="security-info">
                                        <span className="security-icon">🔒</span>
                                        Безопасный платеж через ЮKassa
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PaymentPage;